import { CircularProgress, FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorMessage, getPlayers, Player } from '@/lib/api';

interface PlayerSelectProps {
  value: number | undefined;
  onChange: (playerId: number | undefined) => void;
  required?: boolean;
  error?: string;
  label?: string;
  fullWidth?: boolean;
  disabled?: boolean;
}

/**
 * Player selection component
 * Dropdown for player selection used in program creation/editing screens
 */
export const PlayerSelect: React.FC<PlayerSelectProps> = ({
  value,
  onChange,
  required = false,
  error,
  label,
  fullWidth = false,
  disabled = false,
}) => {
  const { t } = useTranslation('players');

  // Player list
  const [players, setPlayers] = useState<Player[]>([]);
  // Loading state
  const [loading, setLoading] = useState(true);
  // Error state
  const [loadError, setLoadError] = useState<string | null>(null);

  // Default label
  const defaultLabel = t('selection.label');

  // Fetch player list (only on initial render)
  useEffect(() => {
    const fetchPlayers = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        // Get player list from API
        const response = await getPlayers(100, 0); // Get up to 100 items
        setPlayers(response.data || []);
      } catch (err) {
        console.error('Failed to fetch players:', err);
        setLoadError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    fetchPlayers();
  }, []); // Empty dependency array to run only once

  // Set initial value (when player list, onChange, or value changes)
  useEffect(() => {
    if (players.length > 0 && value === undefined) {
      onChange(players[0].id);
    }
  }, [players, onChange, value]);

  // Player change handler
  const handlePlayerChange = (event: SelectChangeEvent<string | number>) => {
    const selectedValue = event.target.value;

    if (selectedValue === '') {
      onChange(undefined);
    } else {
      onChange(Number(selectedValue));
    }
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      required={required}
      error={!!error || !!loadError}
      disabled={disabled || loading}
    >
      <InputLabel id="player-select-label">{label || defaultLabel}</InputLabel>
      <Select
        labelId="player-select-label"
        value={value === undefined ? '' : value}
        label={label || defaultLabel}
        onChange={handlePlayerChange}
        startAdornment={loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
      >
        {players.map((player) => (
          <MenuItem key={player.id} value={player.id}>
            {player.name || player.playerCode}
          </MenuItem>
        ))}
      </Select>
      {(error || loadError) && (
        <FormHelperText>{error || loadError}</FormHelperText>
      )}
    </FormControl>
  );
};

export default PlayerSelect;
